/*
 * Both tokens live in localStorage on purpose. Collector auth is header based
 * (X-HKP-Collector-Token) and the backend sets no httpOnly cookie for collectors, so there is
 * nowhere safer in this stack that also survives closing the tab, which "stay signed in" needs.
 * The exposure is XSS on our own origin; the refresh token rotates on every use and sign-out
 * ends it on the server, which limits how long a stolen copy works.
 */
const COLLECTOR_TOKEN_KEY = "hkp_collector_token";
const COLLECTOR_REFRESH_KEY = "hkp_collector_refresh";
/** Per tab: why the last session ended, when the person did not end it themselves. */
const SESSION_END_KEY = "hkp_collector_session_end";

export const isCollectorEnabled = (): boolean =>
  process.env.REACT_APP_COLLECTOR_ENABLED === "true";

/** Login helper copy (prefilled test phone / 1234). True only for loc builds. */
export const isCollectorLocalDevHint = (): boolean =>
  process.env.REACT_APP_ENV === "loc";

export const getCollectorToken = (): string | null =>
  localStorage.getItem(COLLECTOR_TOKEN_KEY);

export const getCollectorRefreshToken = (): string | null =>
  localStorage.getItem(COLLECTOR_REFRESH_KEY);

/**
 * Stores a new session. The refresh token is always replaced (or dropped), so a token from
 * one account is never renewed into another one.
 */
export const setCollectorToken = (token: string, refreshToken?: string | null): void => {
  localStorage.setItem(COLLECTOR_TOKEN_KEY, token);
  try {
    sessionStorage.removeItem(SESSION_END_KEY);
  } catch {
    // ignore
  }
  if (refreshToken) {
    localStorage.setItem(COLLECTOR_REFRESH_KEY, refreshToken);
  } else {
    localStorage.removeItem(COLLECTOR_REFRESH_KEY);
  }
};

/** Why this device's session ended: the person signed out, or the server refused to renew it. */
export type CollectorSessionEndReason = "signed-out" | "refused" | "number-changed";

type SessionClearedListener = (reason: CollectorSessionEndReason) => void;
const sessionClearedListeners = new Set<SessionClearedListener>();

/**
 * Called every time this tab ends the collector session (sign-out, refused renewal, number change),
 * so work tied to that session (uploads, background jobs) can stop. Returns an unsubscribe.
 */
export const onCollectorSessionCleared = (listener: SessionClearedListener): (() => void) => {
  sessionClearedListeners.add(listener);
  return () => {
    sessionClearedListeners.delete(listener);
  };
};

/**
 * Why the last session on this tab ended, if the person did not choose it (read once by the
 * sign-in page so it can explain instead of surprising them).
 */
export const takeCollectorSessionEndReason = (): CollectorSessionEndReason | null => {
  try {
    const reason = sessionStorage.getItem(SESSION_END_KEY) as CollectorSessionEndReason | null;
    sessionStorage.removeItem(SESSION_END_KEY);
    return reason === "refused" || reason === "number-changed" ? reason : null;
  } catch {
    return null;
  }
};

/** The one way a collector session ends on this device. Every caller goes through here. */
export const clearCollectorToken = (reason: CollectorSessionEndReason = "signed-out"): void => {
  localStorage.removeItem(COLLECTOR_TOKEN_KEY);
  localStorage.removeItem(COLLECTOR_REFRESH_KEY);
  try {
    if (reason === "signed-out") sessionStorage.removeItem(SESSION_END_KEY);
    else sessionStorage.setItem(SESSION_END_KEY, reason);
  } catch {
    // Private mode: the sign-in page just shows no explanation.
  }
  sessionClearedListeners.forEach((listener) => {
    try {
      listener(reason);
    } catch {
      // A listener's failure must not keep the session alive.
    }
  });
};

export const collectorAuthHeaders = (): Record<string, string> => {
  const token = getCollectorToken();
  return token ? { "X-HKP-Collector-Token": token } : {};
};

/**
 * Only My Collection pages on this site are allowed as a place to return to after sign-in:
 * "/my-collection" itself or a path under it. Rejects other origins ("//host", schemes),
 * backslashes, look-alikes ("/my-collectionX"), ".." segments and the sign-in page itself.
 * The path is checked both as written and decoded, so "%2e%2e", "%2f" and "%5c" tricks fail too;
 * a path that does not decode is refused.
 */
export const safeCollectorNext = (raw: string | null | undefined): string | null => {
  if (!raw || !/^\/my-collection(?:[/?#]|$)/.test(raw)) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\\\u0000-\u001f]/.test(raw)) return null;
  const path = raw.split(/[?#]/)[0];
  // Encoded dots, slashes and backslashes have no place in a My Collection path.
  if (/%(?:2e|2f|5c)/i.test(path)) return null;
  let decoded: string;
  try {
    decoded = decodeURIComponent(path);
  } catch {
    return null;
  }
  for (const candidate of [path, decoded]) {
    if (!/^\/my-collection(?:\/|$)/.test(candidate)) return null;
    // eslint-disable-next-line no-control-regex
    if (/[\\\u0000-\u001f]/.test(candidate)) return null;
    if (candidate.includes("//")) return null;
    if (candidate.split("/").some((part) => part === ".." || part === ".")) return null;
    if (candidate === "/my-collection/login" || candidate.startsWith("/my-collection/login/")) return null;
  }
  return raw;
};

/** Sign-in page that brings the person back to `from` (usually the page they were on). */
export const collectorLoginPath = (from?: string): string => {
  const next = safeCollectorNext(from);
  return next ? `/my-collection/login?next=${encodeURIComponent(next)}` : "/my-collection/login";
};

/** WhatsApp help link for My Collection, or null when no support number is configured for this build. */
export const collectorHelpWhatsAppUrl = (): string | null => {
  const number = whatsAppNumber(process.env.REACT_APP_MC_HELP_WHATSAPP);
  return number ? `https://wa.me/${number}?text=${encodeURIComponent("Hi, I need help with My Collection.")}` : null;
};

/**
 * wa.me wants the country code and digits only. An Indian mobile may be written as 98765 43210,
 * 098765 43210, 91 98765 43210 or +91 98765 43210; any other number must already carry its
 * country code (11 to 15 digits). Returns null when the value is not a usable number.
 */
export const whatsAppNumber = (raw?: string | null): string | null => {
  const digits = String(raw || "").replace(/\D/g, "").replace(/^00/, "");
  const indianMobile = /^[6-9]\d{9}$/;
  if (indianMobile.test(digits)) return `91${digits}`;
  if (digits.length === 11 && digits.startsWith("0") && indianMobile.test(digits.slice(1))) return `91${digits.slice(1)}`;
  if (digits.length === 12 && digits.startsWith("91")) return indianMobile.test(digits.slice(2)) ? digits : null;
  return digits.length >= 11 && digits.length <= 15 && !digits.startsWith("0") ? digits : null;
};
